import { Flex, Text, Tooltip } from '@mantine/core';
import {
	DocumentWorkflowInstanceStatus,
	InvestmentDetailsLegalDocument,
	InvestmentDetailsLegalDocumentSigner,
} from '@dealstack/shared/lib/generated';
import { InitialsStatusBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InitialsStatusBadge/InitialsStatusBadge.tsx';
import { UnassignedRoleBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/UnassignedRoleBadge.tsx';
import { InvestmentDocumentCardProps } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentDocumentCard/InvestmentDocumentCard.types.ts';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';

interface Props {
	document: InvestmentDetailsLegalDocument;
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	showSigners?: boolean;
	onManageDocument?: (document: InvestmentDetailsLegalDocument) => void;
	onUnassignedSignerClick?: (documentId: string) => void;
}

export const splitDisplayNameForInitials = (displayName: string): [string, string] => {
	const [firstName = '', lastName = ''] = displayName.trim().split(/\s+/);
	return [firstName, lastName];
};

export const getDocumentStatusProps = (
	status: DocumentWorkflowInstanceStatus,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): {
	text: string;
	statusColor: string;
	statusTextColor: string;
} => {
	switch (status) {
		case DocumentWorkflowInstanceStatus.CANCELLED:
		case DocumentWorkflowInstanceStatus.EXPIRED:
		case DocumentWorkflowInstanceStatus.ERROR:
			return {
				text: tMipManage('investment_legal_documents.not_generated'),
				statusColor: 'gray.4',
				statusTextColor: 'dark.4',
			};
		case DocumentWorkflowInstanceStatus.PENDING_ADDITIONAL_SIGNATURES:
			return {
				text: tMipManage('investment_legal_documents.awaiting_signatures'),
				statusColor: 'gray.4',
				statusTextColor: 'dark.4',
			};
		case DocumentWorkflowInstanceStatus.PENDING:
		case DocumentWorkflowInstanceStatus.IN_PROGRESS:
		case DocumentWorkflowInstanceStatus.PENDING_FORM_SUBMISSION:
			return {
				text: tMipManage('investment_legal_documents.review'),
				statusColor: 'orange.8',
				statusTextColor: 'orange.8',
			};
		case DocumentWorkflowInstanceStatus.COMPLETED:
			return {
				text: tMipManage('investment_legal_documents.complete'),
				statusColor: 'teal.8',
				statusTextColor: 'teal.8',
			};
		default:
			return {
				text: 'Unknown status',
				statusColor: 'gray.4',
				statusTextColor: 'dark.4',
			};
	}
};

export const buildDocumentCardProps = ({
	document,
	tMipManage,
	showSigners = true,
	onUnassignedSignerClick,
}: Props): InvestmentDocumentCardProps => {
	const documentName = document.name;

	const {
		text: statusText,
		statusColor,
		statusTextColor,
	} = getDocumentStatusProps(document.status, tMipManage);

	if (!document.signers.length) {
		return {
			documentName,
			subtitle: tMipManage('investment_legal_documents.will_be_sent'),
			statusText,
			statusColor,
			statusTextColor,
		};
	}

	const customSubtitle = showSigners ? (
		<Flex gap="1rem" mt="0.25rem" wrap="nowrap">
			{renderSigners({
				signers: document.signers,
				documentId: document.id,
				onUnassignedSignerClick,
				maxVisible: MAX_VISIBLE_SIGNERS_CARD,
			})}
		</Flex>
	) : undefined;

	return {
		documentName,
		customSubtitle,
		statusText,
		statusColor,
		statusTextColor,
	};
};

export const MAX_VISIBLE_SIGNERS_CARD = 3;
export const MAX_VISIBLE_SIGNERS_TABLE = 4;

interface RenderSignersOpts {
	signers: InvestmentDetailsLegalDocumentSigner[];
	documentId: string;
	onUnassignedSignerClick?: (documentId: string) => void;
	maxVisible: number;
}

export const renderSigners = ({
	signers,
	documentId,
	onUnassignedSignerClick,
	maxVisible,
}: RenderSignersOpts) => {
	const visible = signers.slice(0, maxVisible);
	const overflow = signers.slice(maxVisible);
	const OVERFLOW_CAP = 3;
	const overflowSigners = overflow
		.slice(0, OVERFLOW_CAP)
		.map((s) => s.displayName ?? s.roleName)
		.filter(Boolean)
		.join(', ');
	const overflowLabel =
		overflow.length > OVERFLOW_CAP ? `${overflowSigners}, ...` : overflowSigners;
	const overflowHasUnclaimed = overflow.some((s) => !s.displayName);
	const overflowClickable = overflowHasUnclaimed && !!onUnassignedSignerClick;

	return (
		<>
			{visible.map((signer) => {
				if (!signer.displayName) {
					return (
						<UnassignedRoleBadge
							key={signer.id}
							roleName={signer.roleName}
							onClick={
								onUnassignedSignerClick
									? () => onUnassignedSignerClick(documentId)
									: undefined
							}
						/>
					);
				}
				const [firstName, lastName] = splitDisplayNameForInitials(signer.displayName);
				return (
					<InitialsStatusBadge
						key={signer.id}
						firstName={firstName}
						lastName={lastName}
						status={signer.status}
					/>
				);
			})}
			{overflow.length > 0 && (
				<Tooltip label={overflowLabel} withArrow multiline>
					<Flex
						align="center"
						role={overflowClickable ? 'button' : undefined}
						tabIndex={overflowClickable ? 0 : undefined}
						onClick={
							overflowClickable
								? (event) => {
										event.stopPropagation();
										onUnassignedSignerClick?.(documentId);
									}
								: undefined
						}
						onKeyDown={
							overflowClickable
								? (event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											onUnassignedSignerClick?.(documentId);
										}
									}
								: undefined
						}
						style={{
							flexShrink: 0,
							cursor: overflowClickable ? 'pointer' : 'default',
						}}
					>
						<Text
							fz="sm"
							fw={600}
							c={overflowClickable ? 'blue.6' : 'dark.0'}
							style={{ whiteSpace: 'nowrap' }}
						>
							+{overflow.length}
						</Text>
					</Flex>
				</Tooltip>
			)}
		</>
	);
};
